/**
 * Merges the four built-in activity-bar entries with extension-contributed
 * ones, honoring `order.after`/`order.before` hints. Built-ins are never
 * rewritten as extensions — this only unifies the ordering/visibility layer
 * so ActivityBar.js can render a single `.map()` instead of hardcoded JSX.
 */

export const BUILTIN_ORDER = ['collections', 'history', 'git', 'teams'];

/**
 * @param {Array<{ id: string, order?: { after?: string, before?: string } }>} extensionEntries
 * @returns {string[]} merged id order, builtins first, extensions inserted
 *   relative to their hint, falling back to append-at-end if the referenced
 *   id isn't in the list yet (never silently dropped).
 */
export function mergeActivityBarOrder(extensionEntries) {
  const order = [...BUILTIN_ORDER];
  for (const entry of extensionEntries) {
    let idx = order.length;
    if (entry.order?.after && order.includes(entry.order.after)) {
      idx = order.indexOf(entry.order.after) + 1;
    } else if (entry.order?.before && order.includes(entry.order.before)) {
      idx = order.indexOf(entry.order.before);
    }
    order.splice(idx, 0, entry.id);
  }
  return order;
}

/**
 * @param {Array<{ id, title, icon, isVisible: (authState) => boolean, kind: 'builtin' }>} builtinDefs
 * @param {Array<{ id, extensionId, title, icon, order }>} extensionEntries
 * @param {{ isAuthenticated: boolean, isAdmin: boolean }} authState
 * @returns merged, order-sorted, visibility-filtered list ready to render
 */
export function getMergedActivityBarItems(builtinDefs, extensionEntries, authState) {
  const order = mergeActivityBarOrder(extensionEntries);
  const byId = new Map();
  for (const def of builtinDefs) byId.set(def.id, { ...def, kind: 'builtin' });
  for (const entry of extensionEntries) {
    byId.set(entry.id, {
      id: entry.id,
      extensionId: entry.extensionId,
      title: entry.title,
      icon: entry.icon,
      kind: 'extension',
      isVisible: () => true,
    });
  }

  return order
    .map((id) => byId.get(id))
    .filter((item) => item && item.isVisible(authState));
}
